
import React from "react";

import {
    Document,
    Page,
    Text,
    View,
    Image,
    Svg,
    Line,
    Polygon,
    Rect,
    Circle,
    Path,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import SCHOOL_LOGO from "./RoyalLogo.png";

/* ==========================================================================
   ROYAL PRIESTHOOD ACADEMY SCHOOL SYSTEM
   Premium Academic Completion Certificate
   Theme: Royal Blue • Sky Blue • Deep Blue
   A4 Landscape • Decorative Frames • Academic Seal
   ========================================================================== */

const CAYEP_NAME = "ROYAL PRIESTHOOD ACADEMY";
const CAYEP_SUBTITLE = "SCHOOL SYSTEM";
const CAYEP_ADDRESS =
    "NEW JERUSALEM COMMUNITY, JOHNSONVILLE, MONROVIA, LIBERIA";

const CAYEP_CERTIFICATE_TITLE = "CERTIFICATE OF COMPLETION";
const CAYEP_CREDENTIAL = "OFFICIAL ACADEMIC CERTIFICATE";

const COLORS = {
    // Primary school colors
    navy: "#071B49",
    blue: "#123B8B",
    royal: "#2455C5",
    sky: "#65B8F3",
    lightSky: "#DCEEFF",
    paleBlue: "#F0F7FF",
    iceBlue: "#F8FBFF",

    // Supporting colors
    white: "#FFFFFF",
    silver: "#D6E2F0",
    gray: "#AABBD0",
    text: "#172B4D",
    muted: "#647795",
    border: "#C8D9ED",
    deepBorder: "#8CA9D0",

    // Restrained metallic-style accent
    gold: "#E5C66B",
    goldLight: "#F5E5A8",

    paper: "#FFFFFF",
    soft: "#F4F8FE",
    softBlue: "#EAF3FF",
};

const getDisplayValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.title ||
            value.label ||
            value.value ||
            ""
        );
    }

    return String(value);
};

const getStudentName = (student) => {
    return (
        getDisplayValue(student?.full_name) ||
        getDisplayValue(student?.name) ||
        getDisplayValue(student?.student_name) ||
        "Graduate"
    );
};

const getClassName = (gradeClass) => {
    return getDisplayValue(gradeClass?.name) || "Academic Program";
};

const getDivision = (gradeClass) => {
    return getDisplayValue(gradeClass?.division);
};

const getAcademicYear = (academicYear) => {
    return getDisplayValue(academicYear?.name) || "Academic Year";
};

const getCertificateNumber = (student, academicYear) => {
    const year = getAcademicYear(academicYear);

    const yearDigits =
        String(year).replace(/[^0-9]/g, "").slice(-4) || "2026";

    const studentId =
        student?.id || student?.student_id || "001";

    return `RPA-${yearDigits}-${studentId}`;
};

/* ==========================================================================
   CERTIFICATE STYLES
   ========================================================================== */

const styles = StyleSheet.create({
    page: {
        width: "100%",
        height: "100%",
        backgroundColor: COLORS.iceBlue,
        fontFamily: "Helvetica",
        padding: 23,
    },

    outerFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 3,
        borderColor: COLORS.navy,
        padding: 4,
        backgroundColor: COLORS.white,
    },

    goldFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 1.5,
        borderColor: COLORS.gold,
        padding: 3,
        backgroundColor: COLORS.white,
    },

    innerFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 1.3,
        borderColor: COLORS.royal,
        backgroundColor: COLORS.paper,
        overflow: "hidden",
        position: "relative",
    },

    topBand: {
        width: "100%",
        height: 10,
        flexDirection: "row",
    },

    topBandGold: {
        width: "12%",
        backgroundColor: COLORS.sky,
    },

    topBandBlue: {
        width: "46%",
        backgroundColor: COLORS.navy,
    },

    topBandOrange: {
        width: "25%",
        backgroundColor: COLORS.royal,
    },

    topBandGoldEnd: {
        width: "17%",
        backgroundColor: COLORS.gold,
    },

    bottomBand: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 8,
        flexDirection: "row",
    },

    bottomNavy: {
        width: "42%",
        backgroundColor: COLORS.navy,
    },

    bottomBlue: {
        width: "30%",
        backgroundColor: COLORS.royal,
    },

    bottomGold: {
        width: "18%",
        backgroundColor: COLORS.sky,
    },

    bottomOrange: {
        width: "10%",
        backgroundColor: COLORS.gold,
    },

    cornerTopLeft: {
        position: "absolute",
        left: 0,
        top: 10,
    },

    cornerTopRight: {
        position: "absolute",
        right: 0,
        top: 10,
    },

    cornerBottomLeft: {
        position: "absolute",
        left: 0,
        bottom: 8,
    },

    cornerBottomRight: {
        position: "absolute",
        right: 0,
        bottom: 8,
    },

    content: {
        flex: 1,
        paddingHorizontal: 38,
        paddingTop: 13,
        paddingBottom: 13,
    },

    header: {
        width: "100%",
        minHeight: 59,
        flexDirection: "row",
        alignItems: "center",
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        paddingBottom: 5,
    },

    logoArea: {
        width: 75,
        height: 57,
        alignItems: "flex-start",
        justifyContent: "center",
    },

    logo: {
        width: 53,
        height: 53,
        objectFit: "contain",
    },

    organizationArea: {
        flex: 1,
        paddingLeft: 3,
        paddingRight: 12,
    },

    organization: {
        color: COLORS.navy,
        fontSize: 16,
        fontWeight: "bold",
        letterSpacing: 1.5,
    },

    organizationSub: {
        marginTop: 3,
        color: COLORS.royal,
        fontSize: 7,
        fontWeight: "bold",
        letterSpacing: 2,
    },

    address: {
        marginTop: 4,
        color: COLORS.muted,
        fontSize: 6.1,
        letterSpacing: 0.45,
    },

    headerRuleArea: {
        width: 115,
        alignItems: "flex-end",
        justifyContent: "center",
    },

    headerRuleTop: {
        width: 108,
        height: 3,
        backgroundColor: COLORS.navy,
        marginBottom: 4,
    },

    headerRuleMiddle: {
        width: 83,
        height: 2,
        backgroundColor: COLORS.royal,
        marginBottom: 4,
    },

    headerRuleBottom: {
        width: 57,
        height: 2,
        backgroundColor: COLORS.sky,
    },

    headingArea: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 8,
    },

    credential: {
        color: COLORS.royal,
        fontSize: 6.6,
        fontWeight: "bold",
        letterSpacing: 2.1,
        textAlign: "center",
    },

    certificateTitle: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 24,
        fontWeight: "bold",
        letterSpacing: 1.4,
        textAlign: "center",
    },

    headingDecoration: {
        width: 290,
        height: 12,
        marginTop: 2,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    headingLineLeft: {
        width: 130,
        height: 1,
        backgroundColor: COLORS.royal,
    },

    headingLineRight: {
        width: 130,
        height: 1,
        backgroundColor: COLORS.royal,
    },

    headingDiamond: {
        width: 8,
        height: 8,
        marginHorizontal: 9,
        backgroundColor: COLORS.sky,
        borderWidth: 1,
        borderColor: COLORS.royal,
        transform: [{ rotate: "45deg" }],
    },

    recipientSection: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
        paddingHorizontal: 15,
    },

    bodyIntro: {
        color: COLORS.muted,
        fontSize: 7.5,
        letterSpacing: 0.55,
        textAlign: "center",
    },

    studentName: {
        marginTop: 5,
        color: COLORS.navy,
        fontSize: 28,
        fontWeight: "bold",
        letterSpacing: 0.8,
        textAlign: "center",
        maxWidth: 575,
    },

    studentNameDecoration: {
        width: 390,
        height: 12,
        marginTop: 2,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },

    nameLineLeft: {
        width: 157,
        height: 1,
        backgroundColor: COLORS.sky,
    },

    nameLineRight: {
        width: 157,
        height: 1,
        backgroundColor: COLORS.sky,
    },

    nameDiamond: {
        width: 8,
        height: 8,
        marginHorizontal: 9,
        backgroundColor: COLORS.royal,
        transform: [{ rotate: "45deg" }],
    },

    recognitionArea: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
    },

    recognition: {
        maxWidth: 530,
        color: COLORS.text,
        fontSize: 7.4,
        lineHeight: 1.4,
        textAlign: "center",
    },

    programSection: {
        width: "100%",
        minHeight: 51,
        marginTop: 7,
        flexDirection: "row",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.soft,
    },

    programLeft: {
        width: 7,
        backgroundColor: COLORS.royal,
    },

    programMiddle: {
        flex: 1,
        paddingVertical: 6,
        paddingHorizontal: 14,
        justifyContent: "center",
    },

    programTopLabel: {
        color: COLORS.royal,
        fontSize: 6,
        fontWeight: "bold",
        letterSpacing: 1.3,
    },

    programName: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 10.5,
        fontWeight: "bold",
        letterSpacing: 0.3,
    },

    division: {
        marginTop: 2,
        color: COLORS.muted,
        fontSize: 6.5,
    },

    programRight: {
        width: 128,
        borderLeftWidth: 1,
        borderLeftColor: COLORS.border,
        backgroundColor: COLORS.softBlue,
        alignItems: "center",
        justifyContent: "center",
    },

    yearLabel: {
        color: COLORS.royal,
        fontSize: 5.7,
        fontWeight: "bold",
        letterSpacing: 1,
    },

    yearValue: {
        marginTop: 4,
        color: COLORS.navy,
        fontSize: 9.5,
        fontWeight: "bold",
        letterSpacing: 0.4,
    },

    lowerArea: {
        width: "100%",
        minHeight: 45,
        marginTop: 5,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    credentialBox: {
        width: 162,
        height: 32,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.white,
        justifyContent: "center",
        paddingHorizontal: 10,
    },

    credentialLabel: {
        color: COLORS.muted,
        fontSize: 5.2,
        letterSpacing: 0.8,
    },

    credentialValue: {
        marginTop: 3,
        color: COLORS.navy,
        fontSize: 6.8,
        fontWeight: "bold",
        letterSpacing: 0.5,
    },

    badgeArea: {
        flex: 1,
        height: 45,
        alignItems: "center",
        justifyContent: "center",
    },

    badge: {
        width: 46,
        height: 46,
    },

    statusBox: {
        width: 162,
        height: 32,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.white,
        justifyContent: "center",
        alignItems: "flex-end",
        paddingHorizontal: 10,
    },

    statusLabel: {
        color: COLORS.muted,
        fontSize: 5.2,
        letterSpacing: 0.8,
    },

    statusValue: {
        marginTop: 3,
        color: COLORS.royal,
        fontSize: 6.5,
        fontWeight: "bold",
        letterSpacing: 0.35,
    },

    signatures: {
        width: "100%",
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginTop: 5,
    },

    signature: {
        width: 170,
        alignItems: "center",
    },

    signatureLine: {
        width: 145,
        borderTopWidth: 1,
        borderTopColor: COLORS.royal,
        marginBottom: 3,
    },

    signatureName: {
        color: COLORS.navy,
        fontSize: 6.1,
        fontWeight: "bold",
        letterSpacing: 0.3,
        textAlign: "center",
    },

    signatureRole: {
        marginTop: 2,
        color: COLORS.royal,
        fontSize: 5,
        letterSpacing: 0.35,
        textAlign: "center",
    },

    centerMark: {
        width: 145,
        alignItems: "center",
        justifyContent: "center",
    },

    centerMarkText: {
        color: COLORS.royal,
        fontSize: 5.2,
        fontWeight: "bold",
        letterSpacing: 0.85,
        textAlign: "center",
    },

    centerMarkSub: {
        marginTop: 3,
        color: COLORS.muted,
        fontSize: 4.7,
        letterSpacing: 0.4,
        textAlign: "center",
    },

    footerNote: {
        marginTop: 4,
        alignItems: "center",
    },

    footerNoteText: {
        color: COLORS.muted,
        fontSize: 4.5,
        letterSpacing: 0.65,
        textAlign: "center",
    },
});

/* ==========================================================================
   DECORATIVE CORNERS
   Original triangular corner graphics retained and enhanced in blue.
   ========================================================================== */

const CornerDecoration = ({
    flipX = false,
    flipY = false,
}) => {
    const transform = [];

    if (flipX) {
        transform.push({ scaleX: -1 });
    }

    if (flipY) {
        transform.push({ scaleY: -1 });
    }

    return (
        <Svg
            width="105"
            height="82"
            viewBox="0 0 105 82"
            style={{ transform }}
        >
            <Polygon
                points="0,0 105,0 0,82"
                fill={COLORS.navy}
            />

            <Polygon
                points="0,0 78,0 0,60"
                fill={COLORS.royal}
            />

            <Polygon
                points="0,0 46,0 0,37"
                fill={COLORS.sky}
            />

            <Line
                x1="13"
                y1="13"
                x2="75"
                y2="13"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.48"
            />

            <Line
                x1="13"
                y1="13"
                x2="13"
                y2="62"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.38"
            />

            <Circle
                cx="16"
                cy="16"
                r="3"
                fill={COLORS.gold}
            />
        </Svg>
    );
};

/* ==========================================================================
   PREMIUM ACADEMIC SEAL
   Original seal concept preserved with a refined royal-blue palette.
   ========================================================================== */

const PremiumBadge = () => {
    return (
        <Svg
            width="46"
            height="46"
            viewBox="0 0 92 92"
        >
            <Circle
                cx="46"
                cy="46"
                r="43"
                fill={COLORS.navy}
            />

            <Circle
                cx="46"
                cy="46"
                r="39"
                fill="none"
                stroke={COLORS.gold}
                strokeWidth="2"
            />

            <Circle
                cx="46"
                cy="46"
                r="34"
                fill={COLORS.royal}
            />

            <Circle
                cx="46"
                cy="46"
                r="29"
                fill="none"
                stroke={COLORS.sky}
                strokeWidth="1.3"
            />

            <Polygon
                points="
                    46,17
                    51,31
                    66,31
                    54,40
                    58,55
                    46,47
                    34,55
                    38,40
                    26,31
                    41,31
                "
                fill={COLORS.gold}
            />

            <Circle
                cx="46"
                cy="38"
                r="9"
                fill={COLORS.navy}
            />

            <Path
                d="M40 38 L44 42 L52 33"
                fill="none"
                stroke={COLORS.white}
                strokeWidth="2.5"
            />

            <Circle
                cx="46"
                cy="46"
                r="41.5"
                fill="none"
                stroke={COLORS.white}
                strokeWidth="0.5"
                opacity="0.4"
            />
        </Svg>
    );
};

/* ==========================================================================
   CERTIFICATE PAGE
   ========================================================================== */

const CertificatePage = ({
    student,
    gradeClass,
    academicYear,
}) => {
    const studentName = getStudentName(student);
    const className = getClassName(gradeClass);
    const division = getDivision(gradeClass);
    const year = getAcademicYear(academicYear);

    const certificateNumber = getCertificateNumber(
        student,
        academicYear
    );

    return (
        <Document>
            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
            >
                <View style={styles.outerFrame}>
                    <View style={styles.goldFrame}>
                        <View style={styles.innerFrame}>
                            <View style={styles.topBand}>
                                <View style={styles.topBandGold} />
                                <View style={styles.topBandBlue} />
                                <View style={styles.topBandOrange} />
                                <View style={styles.topBandGoldEnd} />
                            </View>

                            <View style={styles.cornerTopLeft}>
                                <CornerDecoration />
                            </View>

                            <View style={styles.cornerTopRight}>
                                <CornerDecoration flipX />
                            </View>

                            <View style={styles.cornerBottomLeft}>
                                <CornerDecoration flipY />
                            </View>

                            <View style={styles.cornerBottomRight}>
                                <CornerDecoration flipX flipY />
                            </View>

                            <View style={styles.content}>
                                {/* SCHOOL HEADER */}
                                <View style={styles.header}>
                                    <View style={styles.logoArea}>
                                        <Image
                                            src={SCHOOL_LOGO}
                                            style={styles.logo}
                                        />
                                    </View>

                                    <View style={styles.organizationArea}>
                                        <Text style={styles.organization}>
                                            {CAYEP_NAME}
                                        </Text>

                                        <Text style={styles.organizationSub}>
                                            {CAYEP_SUBTITLE}
                                        </Text>

                                        <Text style={styles.address}>
                                            {CAYEP_ADDRESS}
                                        </Text>
                                    </View>

                                    <View style={styles.headerRuleArea}>
                                        <View style={styles.headerRuleTop} />
                                        <View style={styles.headerRuleMiddle} />
                                        <View style={styles.headerRuleBottom} />
                                    </View>
                                </View>

                                {/* CERTIFICATE TITLE */}
                                <View style={styles.headingArea}>
                                    <Text style={styles.credential}>
                                        {CAYEP_CREDENTIAL}
                                    </Text>

                                    <Text style={styles.certificateTitle}>
                                        {CAYEP_CERTIFICATE_TITLE}
                                    </Text>

                                    <View style={styles.headingDecoration}>
                                        <View style={styles.headingLineLeft} />
                                        <View style={styles.headingDiamond} />
                                        <View style={styles.headingLineRight} />
                                    </View>
                                </View>

                                {/* GRADUATE */}
                                <View style={styles.recipientSection}>
                                    <Text style={styles.bodyIntro}>
                                        This academic certificate is proudly
                                        presented to
                                    </Text>

                                    <Text style={styles.studentName}>
                                        {studentName}
                                    </Text>

                                    <View style={styles.studentNameDecoration}>
                                        <View style={styles.nameLineLeft} />
                                        <View style={styles.nameDiamond} />
                                        <View style={styles.nameLineRight} />
                                    </View>
                                </View>

                                {/* RECOGNITION STATEMENT */}
                                <View style={styles.recognitionArea}>
                                    <Text style={styles.recognition}>
                                        In recognition of successfully
                                        completing the academic requirements
                                        of the program and demonstrating
                                        commitment to learning, personal
                                        development, and the application
                                        of the knowledge and skills covered
                                        within the academic program.
                                    </Text>
                                </View>

                                {/* PROGRAM AND ACADEMIC YEAR */}
                                <View style={styles.programSection}>
                                    <View style={styles.programLeft} />

                                    <View style={styles.programMiddle}>
                                        <Text style={styles.programTopLabel}>
                                            ACADEMIC PROGRAM
                                        </Text>

                                        <Text style={styles.programName}>
                                            {className}
                                        </Text>

                                        {division ? (
                                            <Text style={styles.division}>
                                                {division}
                                            </Text>
                                        ) : null}
                                    </View>

                                    <View style={styles.programRight}>
                                        <Text style={styles.yearLabel}>
                                            ACADEMIC YEAR
                                        </Text>

                                        <Text style={styles.yearValue}>
                                            {year}
                                        </Text>
                                    </View>
                                </View>

                                {/* CERTIFICATE NUMBER AND STATUS */}
                                <View style={styles.lowerArea}>
                                    <View style={styles.credentialBox}>
                                        <Text style={styles.credentialLabel}>
                                            CERTIFICATE NUMBER
                                        </Text>

                                        <Text style={styles.credentialValue}>
                                            {certificateNumber}
                                        </Text>
                                    </View>

                                    <View style={styles.badgeArea}>
                                        <PremiumBadge />
                                    </View>

                                    <View style={styles.statusBox}>
                                        <Text style={styles.statusLabel}>
                                            CERTIFICATE STATUS
                                        </Text>

                                        <Text style={styles.statusValue}>
                                            ACADEMICALLY COMPLETED
                                        </Text>
                                    </View>
                                </View>

                                {/* AUTHORIZED SIGNATURES */}
                                <View style={styles.signatures}>
                                    <View style={styles.signature}>
                                        <View style={styles.signatureLine} />

                                        <Text style={styles.signatureName}>
                                            AUTHORIZED DIRECTOR
                                        </Text>

                                        <Text style={styles.signatureRole}>
                                            ROYAL PRIESTHOOD ACADEMY SCHOOL SYSTEM
                                        </Text>
                                    </View>

                                    <View style={styles.centerMark}>
                                        <Text style={styles.centerMarkText}>
                                            ACADEMIC EXCELLENCE
                                        </Text>

                                        <Text style={styles.centerMarkText}>
                                            CERTIFICATE
                                        </Text>

                                        <Text style={styles.centerMarkSub}>
                                            Issued by Royal Priesthood Academy
                                            School System
                                        </Text>
                                    </View>

                                    <View style={styles.signature}>
                                        <View style={styles.signatureLine} />

                                        <Text style={styles.signatureName}>
                                            ACADEMIC / PROGRAM REGISTRAR
                                        </Text>

                                        <Text style={styles.signatureRole}>
                                            ACADEMIC RECORDS &amp; CERTIFICATION
                                        </Text>
                                    </View>
                                </View>

                                {/* OFFICIAL FOOTER */}
                                <View style={styles.footerNote}>
                                    <Text style={styles.footerNoteText}>
                                        ROYAL PRIESTHOOD ACADEMY SCHOOL SYSTEM
                                        {" • "}ACADEMIC EXCELLENCE
                                        {" • "}STUDENT DEVELOPMENT
                                    </Text>
                                </View>
                            </View>

                            <View style={styles.bottomBand}>
                                <View style={styles.bottomNavy} />
                                <View style={styles.bottomBlue} />
                                <View style={styles.bottomGold} />
                                <View style={styles.bottomOrange} />
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

/* ==========================================================================
   PDF DOWNLOAD SERVICE
   Existing public method names and arguments preserved.
   ========================================================================== */

const GraduateCertificatePDF = {
    downloadSingle: async (
        student,
        gradeClass,
        academicYear
    ) => {
        const blob = await pdf(
            <CertificatePage
                student={student}
                gradeClass={gradeClass}
                academicYear={academicYear}
            />
        ).toBlob();

        const fileName =
            `${getStudentName(student).replace(/\s+/g, "_")}` +
            `_Royal_Priesthood_Academy_Certificate.pdf`;

        saveAs(blob, fileName);
    },

    downloadMultiple: async (
        students,
        gradeClass,
        academicYear
    ) => {
        const zip = new JSZip();

        for (const student of students) {
            const blob = await pdf(
                <CertificatePage
                    student={student}
                    gradeClass={gradeClass}
                    academicYear={academicYear}
                />
            ).toBlob();

            const fileName =
                `${getStudentName(student).replace(/\s+/g, "_")}` +
                `_Royal_Priesthood_Academy_Certificate.pdf`;

            zip.file(fileName, blob);
        }

        const zipBlob = await zip.generateAsync({
            type: "blob",
        });

        saveAs(
            zipBlob,
            "Royal_Priesthood_Academy_Certificates.zip"
        );
    },
};

export default GraduateCertificatePDF;
